import { Link, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Clock, MapPin, Star, Users } from 'lucide-react';
import { FormationIcon } from './FormationIcon';

interface FormationCardProps {
  slug: string;
  title: string;
  description: string;
  image: string | null;
  duration_hours: number;
  duration_days: number;
  price_ttc: number;
  category: string;
  mode?: 'presentiel' | 'distanciel' | 'hybride';
}

export function FormationCard({
  slug,
  title,
  description,
  image,
  duration_hours,
  duration_days,
  price_ttc,
  category,
  mode = 'distanciel',
}: FormationCardProps) {
  const { t, i18n } = useTranslation('formations');
  const { locale } = useParams<{ locale: string }>();
  const currentLocale = locale || i18n.language || 'fr';

  return (
    <Card className="overflow-hidden hover:shadow-xl transition-all hover:-translate-y-1 group h-full flex flex-col">
      <Link to={`/${currentLocale}/formations/${slug}`} className="flex flex-col h-full">
        {/* Image */}
        <div className="h-48 bg-gradient-to-br from-aspexit-red/20 to-aspexit-orange/20 flex items-center justify-center relative overflow-hidden">
          {image ? (
            <img
              src={image}
              alt={title}
              className="w-full h-full object-cover transition-transform group-hover:scale-105"
            />
          ) : (
            <div className="flex flex-col items-center justify-center p-6 gap-3">
              <FormationIcon category={category} className="w-20 h-20" />
              <span className="text-aspexit-gray text-sm font-medium">{category}</span>
            </div>
          )}

          {/* QUALIOPI Badge */}
          <div className="absolute top-2 right-2 bg-aspexit-red text-white px-2 py-1 rounded-md flex items-center gap-1 text-xs font-semibold">
            {t('card.qualiopiBadge')}
          </div>
        </div>

        {/* Content */}
        <div className="p-6 flex-1 flex flex-col">
          {/* Category */}
          <Badge
            variant="outline"
            className="mb-3 text-aspexit-red border-aspexit-red w-fit"
          >
            {category}
          </Badge>

          {/* Title */}
          <h3 className="text-xl font-heading font-semibold text-aspexit-black mb-3 group-hover:text-aspexit-red transition-colors line-clamp-2">
            {title}
          </h3>

          {/* Description */}
          <p className="text-sm text-aspexit-gray mb-4 line-clamp-3 flex-1">
            {description}
          </p>

          {/* Meta Info */}
          <div className="grid grid-cols-2 gap-2 mb-4 text-xs text-aspexit-gray">
            <div className="flex items-center gap-1">
              <Clock className="w-3 h-3" />
              <span>
                {duration_days} {duration_days > 1 ? t('card.daysPlural') : t('card.days')} ({duration_hours}h)
              </span>
            </div>
            <div className="flex items-center gap-1">
              <MapPin className="w-3 h-3" />
              <span>{t('card.mode')}</span>
            </div>
          </div>

          {/* Price & CTA */}
          <div className="flex items-center justify-between pt-4 border-t border-border">
            <div>
              <div className="text-2xl font-bold text-aspexit-red">
                {price_ttc}€
              </div>
              <div className="text-xs text-aspexit-gray">{t('card.priceSuffix')}</div>
              <div className="text-xs text-aspexit-green mt-1">{t('card.groupPricing')}</div>
            </div>
            <span className="text-aspexit-red text-sm font-medium group-hover:underline">
              {t('card.learnMore')}
            </span>
          </div>
        </div>
      </Link>
    </Card>
  );
}
